/**
 * EmbersShop — common-rarity cosmetics buyable with Embers.
 * Renders inside the CharacterProfileModal customize flow.
 *
 * Items the player already owns are dimmed + show "Owned." Buying
 * deducts embers atomically and list-appends the item to the right
 * inventory bucket on the server. Refresh stats on success so the
 * UI reflects the new wallet + inventory.
 */
import { useState } from 'react';
import { ShoppingBag, Flame as FlameIcon, Lock } from 'lucide-react';
import { EMBERS_SHOP, type ShopItem } from '@tt/lobby/shop';
import { shopBuy, type CharacterStats } from '../../lib/api';

interface Props {
  charId: string;
  stats: CharacterStats | null;
  onPurchase: () => void; // tells parent to re-fetch stats
}

export function EmbersShop({ charId, stats, onPurchase }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const embers = stats?.embers ?? 0;

  function ownsItem(it: ShopItem): boolean {
    if (!stats) return false;
    const inv = stats.inventory as unknown as Record<string, Array<string | number>>;
    const arr = inv[it.bucket];
    if (!Array.isArray(arr)) return false;
    return arr.includes(it.ref);
  }

  async function buy(it: ShopItem) {
    if (busy) return;
    setBusy(it.id);
    setMsg(null);
    try {
      await shopBuy(charId, it.id);
      setMsg({ kind: 'ok', text: `Bought ${it.label}!` });
      onPurchase();
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === 'insufficient-embers') setMsg({ kind: 'err', text: 'Not enough Embers.' });
      else if (code === 'already-owned') setMsg({ kind: 'err', text: 'Already owned.' });
      else setMsg({ kind: 'err', text: 'Purchase failed.' });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="profile-section embers-shop">
      <header className="embers-shop-head">
        <h3><ShoppingBag size={18} /> Embers shop</h3>
        <span className="embers-wallet">
          <FlameIcon size={14} /> {embers} Embers
        </span>
      </header>
      <p className="muted" style={{ margin: '0 0 0.6rem' }}>
        Common cosmetics for Embers. Earnable items still drop free through play —
        the shop is a shortcut. No real money.
      </p>
      {msg && (
        <div className={`embers-shop-msg ${msg.kind}`}>{msg.text}</div>
      )}
      <div className="embers-shop-grid">
        {EMBERS_SHOP.map((it) => {
          const owned = ownsItem(it);
          const tooPoor = embers < it.price;
          return (
            <article key={it.id} className={`embers-shop-card rarity-${it.rarity}${owned ? ' owned' : ''}${tooPoor && !owned ? ' poor' : ''}`}>
              <div className="embers-shop-card-text">
                <strong>{it.label}</strong>
                <small>{it.description}</small>
              </div>
              <div className="embers-shop-card-action">
                {owned ? (
                  <span className="muted" style={{ fontSize: '0.78rem' }}>Owned</span>
                ) : (
                  <button
                    type="button"
                    className="btn ghost icon-btn embers-shop-buy"
                    onClick={() => void buy(it)}
                    disabled={busy === it.id || tooPoor}
                    title={tooPoor ? `Need ${it.price - embers} more Embers` : `Buy for ${it.price} Embers`}
                  >
                    {tooPoor ? <Lock size={12} /> : <FlameIcon size={12} />}
                    {busy === it.id ? '…' : `${it.price}`}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
